import React from "react";
import "./styles.scss";
import cn from "classnames";
import { TextLink } from "@trussle/external-components";
import { ImageDataLike } from "gatsby-plugin-image";
import { IContentfulImage } from "../../types/IContentfulImage";
import { GatsbyImageOrSvg } from "../../utils/GatsbyImageOrSvg/GatsbyImageOrSvg";
import ArrowForwardOrangeSVG from "./arrow-forward-orange.svg";
import ArrowForwardGreenSVG from "./arrow-forward-green.svg";
import ExpandIconSVG from "./expand-icon.svg";

interface ILink {
    url: string;
    text: string;
}

export interface ICTACard {
    variant?: "orange" | "green";
    title: string;
    subTitle?: string | null;
    withOptions: boolean;
    url?: string;
    links?: ILink[];
    icon?: IContentfulImage & ImageDataLike;
}
const GetVariantArrowIcon: React.FC<{variant?: string}> = ({ variant }) => (
  variant === "orange"
    ? <img src={ArrowForwardOrangeSVG} alt="arrow-forward" />
    : <img src={ArrowForwardGreenSVG} alt="arrow-forward" />
);
const CTACard: React.FC<ICTACard> = ({
  variant = "orange", title, subTitle, withOptions, url, links, icon,
}) => {
  const handleOptionsSelect = (url: string) => {
    window.location.assign(url);
  };
  const handleCardClick = (url: string) => {
    if (!withOptions) window.location.assign(url);
  };
  const cardTitleClassName = cn("cta-card__title", variant);
  return (
    <div
      className={cn("cta-container", { "with-options": withOptions })}
      aria-label={`Call to Action ${title}`}
      role="button"
      onKeyDown={() => handleCardClick(url!)}
      tabIndex={0}
      onClick={() => handleCardClick(url!)}
    >
      <div className={cn("cta-card", variant, { "with-options": withOptions })} data-testid="cta-card">
        <div className={cn("cta-card__content", { "with-options": withOptions })}>
          <section className="cta-card__subtitle">
            {icon
              && <GatsbyImageOrSvg image={icon as IContentfulImage & ImageDataLike} loading="lazy" alt="icon" className="cta-card__subtitle__icon" />}
            <p className={cn("cta-card__subtitle__text", variant)}>{subTitle}</p>
          </section>
          {url
            ? <TextLink className={cardTitleClassName} href={url} target="_blank">{title}</TextLink>
            : <span className={cardTitleClassName}>{title}</span>}
          {!withOptions && (
          <section className="cta-card__link">
            <TextLink href={url} target="_blank">
              Start right now
            </TextLink>
            <GetVariantArrowIcon variant={variant} />
          </section>
          )}
        </div>
        {icon
        && (
        <div className="cta-card__subtitle__icon__mobile">
          <GatsbyImageOrSvg
            image={icon as IContentfulImage & ImageDataLike}
            loading="lazy"
            alt="icon"
          />
        </div>
        )}
        {withOptions
          && (
          <select
            className="cta-card__options__mobile"
            aria-label="cta-card__options__mobile"
            data-testid="cta-card__options__mobile"
            onChange={(e) => handleOptionsSelect(e.target.value)}
          >
            <option hidden disabled selected value="">{title}</option>
            {links && links.map((link, index) => (
              <option
                className="cta-card__option"
                key={index}
                value={link.url}
                data-testid="cta-card__options__mobile_option"
              >
                {link.text}
              </option>
            ))}
          </select>
          )}
        {withOptions && (
        <div className="expand-icon__container">
          <img src={ExpandIconSVG} alt="expand" className="expand-icon" />
        </div>
        )}
      </div>
      {withOptions
            && (
            <div className={cn("cta-card__option-links", variant)} data-testid="cta-card__options">
              {links && links.map((link, index) => (
                <div
                  className="cta-card__option"
                  role="button"
                  onClick={() => handleOptionsSelect(link.url)}
                  onKeyDown={() => handleOptionsSelect(link.url)}
                  tabIndex={0}
                >
                  <TextLink key={index} className="cta-card__option-link" href={link.url} target="_blank">{link.text}</TextLink>
                  <div className="cta-card__option-arrow"><GetVariantArrowIcon variant={variant} /></div>
                </div>
              ))}
            </div>
            )}
    </div>
  );
};

export default CTACard;
